"use client"

import { DirectionIcon, Typography } from "@/shared/ui/atoms"
import { CustomButton } from "@/shared/ui/molecules"
import { Swiper, SwiperSlide } from "swiper/react"
import { Pagination } from "swiper/modules"
import { useQueryHospitalTreatmentCaseSummary } from "@/entities/hospital/hooks/queries"
import { TreatmentCaseSummaryResponse } from "@/entities/hospital/types/type"
import useHospitalUIStore from "@/widgets/hospital/model/store"
import TreatmentCaseSummaryCard from "@/widgets/hospital/ui/molecules/TreatmentCaseSummaryCard"
import { useLocale, useTranslations } from "next-intl"
import { useHospitalOriginId } from "@/entities/hospital/model/hospital-origin-id"

interface TreatmentCaseCarouselProps {
  /**
   * 새 상세 페이지의 섹션 셸 안에 들어갈 때. 제목과 바깥 여백은 섹션이 갖고 있으므로
   * 여기서는 내용만 그린다. 기본값(false)은 기존 상세 페이지의 모습 그대로다.
   */
  embedded?: boolean
}

const TreatmentCaseCarousel = ({ embedded = false }: TreatmentCaseCarouselProps = {}) => {
  const t = useTranslations()
  const locale = useLocale()
  const id = useHospitalOriginId()
  const { setSelectedTab } = useHospitalUIStore()

  const { data: treatmentCaseSummaryList = [] } = useQueryHospitalTreatmentCaseSummary(Number(id))

  const handleClickMore = () => {
    window.scrollTo(0, 0)
    setSelectedTab("treatmentCase")
  }

  if (treatmentCaseSummaryList.length === 0) return null

  return (
    <div className={embedded ? "overflow-hidden" : "overflow-hidden bg-gray-100"}>
      {embedded ? (
        // 전체 목록이 바로 아래 이어지므로 탭으로 넘기던 '더보기'는 갈 곳이 없다
        <Typography as="h3" size={15} lineHeight={20} className="px-4 pb-3 font-bold text-gray-900 sm:px-6">
          {t("treatmentCaseCarousel.title")}
        </Typography>
      ) : (
        <div className="px-4 pt-6 pb-4 flex items-center justify-between">
          <Typography size={16} className="text-gray-800 font-semibold">
            {t("treatmentCaseCarousel.title")}
          </Typography>
          <CustomButton className="flex items-center gap-1" onClick={handleClickMore}>
            <Typography size={14} className="text-gray-500">
              {t("treatmentCaseCarousel.more")}
            </Typography>
            <DirectionIcon size={16} rotate={270} className="text-gray-400 -ml-2" />
          </CustomButton>
        </div>
      )}
      <div className={embedded ? "" : "-mx-4"}>
        <Swiper
          modules={[Pagination]}
          spaceBetween={8}
          slidesPerView={1.17}
          centeredSlides
          pagination={{ clickable: false }}
          className="hospital-treatment-search-swiper"
        >
          {treatmentCaseSummaryList.map((item: TreatmentCaseSummaryResponse, index: number) => (
            <SwiperSlide key={`treatment-case-carousel-${index}`} className="!h-auto">
              <TreatmentCaseSummaryCard
                index={index}
                treatmentCaseId={item.treatmentCaseId}
                treatmentSubjectName={item.treatmentSubjectName}
                title={item.title}
                thumbnail={item.thumbnail}
                href={`/${locale}/treatment-case/${item.treatmentCaseId}`}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  )
}

export default TreatmentCaseCarousel

